/**
 * Opens a new browser tab with a printable calculator report and triggers print.
 *
 * @param {object} opts
 * @param {string} opts.title       – Calculator title
 * @param {string} opts.subtitle    – Calculator subtitle
 * @param {Array}  opts.kpis        – [{ label, value, color }]
 * @param {string} opts.inputsHTML  – HTML for input parameters section (shown on page 1)
 * @param {Array}  opts.pages       – [{ title, html }] – each page starts on a new printed page
 */
function esc(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function safeColor(value) {
  if (!value) return ''
  const safe = /^(#[0-9a-fA-F]{3,8}|rgb\([^)]{0,60}\)|rgba\([^)]{0,80}\)|hsl\([^)]{0,60}\)|hsla\([^)]{0,80}\)|[a-zA-Z]{1,30})$/
  return safe.test(value.trim()) ? value.trim() : ''
}

function safeIdent(s) {
  return String(s ?? '').replace(/[^a-zA-Z0-9-_]/g, '')
}

export function openPrintView({ title, subtitle, address = '', kpis = [], inputsHTML = '', pages = [] }) {
  const kpiHTML = kpis.map(k =>
    `<div class="kpi">
      <div class="kpi-label">${esc(k.label)}</div>
      <div class="kpi-value" style="color:${safeColor(k.color) || '#3C4654'}">${esc(k.value)}</div>
    </div>`
  ).join('<div class="kpi-sep"></div>')

  const pagesHTML = pages.map((pg, i) =>
    `<div class="page${i > 0 ? ' page--break' : ''}">
      <div class="page-title">${esc(pg.title)}</div>
      <div class="page-body">${pg.html}</div>
    </div>`
  ).join('')

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>${esc(title)}${address ? ` - ${esc(address)}` : ''} - Print</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:Inter,system-ui,sans-serif;font-size:13px;color:#3C4654;padding:24px;-webkit-print-color-adjust:exact;print-color-adjust:exact}

/* ── Header ───────────────────────────────────── */
.header{margin-bottom:16px;border-bottom:2px solid #128292;padding-bottom:12px}
.header h1{font-size:22px;font-weight:700;color:#3C4654;margin:0}
.header p{font-size:14px;color:#66707D;margin:2px 0 0;line-height:1.4}
.header-address{font-size:14px;color:#66707D;font-weight:500}
.kpis{display:flex;flex-wrap:wrap;gap:10px;align-items:stretch;margin-top:10px}
.kpi{min-width:60px}
.kpi-label{font-size:11px;font-weight:500;text-transform:uppercase;color:#66707D;letter-spacing:0.3px}
.kpi-value{font-size:18px;font-weight:700;line-height:1.4}
.kpi-sep{width:1px;background:#DFE1E4;align-self:stretch}

/* ── Input Parameters ─────────────────────────── */
.inputs-section{margin-bottom:16px}
.inputs-title{font-size:13px;font-weight:700;text-transform:uppercase;color:#128292;letter-spacing:0.4px;padding:4px 0;border-bottom:1px solid #DFE1E4;margin-bottom:8px}
.inputs-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:4px 24px}
.input-group-title{font-size:12px;font-weight:700;color:#3C4654;margin:6px 0 2px;grid-column:1/-1;border-bottom:1px solid #EFF2F6;padding-bottom:2px}
.input-row{display:flex;justify-content:space-between;font-size:12px;padding:1px 0}
.input-row__label{color:#66707D}
.input-row__value{font-weight:600;color:#3C4654}

/* ── Pages (tabs) ─────────────────────────────── */
.page--break{page-break-before:always}
.page-title{font-size:16px;font-weight:700;text-transform:uppercase;color:#128292;letter-spacing:0.5px;padding:6px 0;border-bottom:2px solid #128292;margin-bottom:12px}
.page-body .section{margin-top:14px}
.page-body .section:first-child{margin-top:0}

/* ── Sections ─────────────────────────────────── */
.section-title{font-size:13px;font-weight:700;text-transform:uppercase;color:#66707D;letter-spacing:0.4px;padding:4px 0;border-bottom:1px solid #DFE1E4;margin-bottom:8px}
.section-body{margin-bottom:4px}

/* ── Cards ────────────────────────────────────── */
.cards{display:flex;flex-wrap:wrap;gap:8px}
.card{flex:1;min-width:120px;border:1px solid #DFE1E4;border-radius:8px;padding:8px 10px}
.card-label{font-size:11px;font-weight:500;text-transform:uppercase;color:#66707D;letter-spacing:0.3px}
.card-value{font-size:16px;font-weight:700;margin:2px 0}
.card-sub{font-size:11px;color:#66707D}

/* ── Pills ────────────────────────────────────── */
.pills{display:flex;flex-wrap:wrap;gap:6px}
.pill{border:1px solid #DFE1E4;border-radius:8px;padding:5px 10px;min-width:90px}
.pill--teal{background:#E2F7FA;border-color:#94C9D1}
.pill--orange{background:#FCD3BF;border-color:#FABC9C}
.pill--rose{background:#F4C2D3;border-color:#CA1551}
.pill-label{font-size:11px;font-weight:500;text-transform:uppercase;color:#66707D;letter-spacing:0.3px}
.pill-value{font-size:14px;font-weight:700}

/* ── Tables ───────────────────────────────────── */
table{width:100%;border-collapse:collapse;font-size:12px}
th{text-align:left;font-size:11px;font-weight:600;text-transform:uppercase;color:#66707D;letter-spacing:0.3px;padding:5px 8px;border-bottom:2px solid #DFE1E4}
td{padding:4px 8px;border-bottom:1px solid #EFF2F6}
tr.highlight td{background:#EFF2F6;font-weight:600}

/* ── P&L rows ─────────────────────────────────── */
.pl-row{display:flex;justify-content:space-between;padding:3px 0;font-size:13px}
.pl-row--indent{padding-left:16px}
.pl-row--bold{font-weight:700}
.pl-divider{border-top:1px solid #DFE1E4;margin:4px 0}
.pl-divider--heavy{border-top:2px solid #3C4654;margin:6px 0;padding-top:4px}

/* ── Layout helpers ───────────────────────────── */
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.timestamp{margin-top:20px;font-size:11px;color:#66707D;text-align:center;border-top:1px solid #DFE1E4;padding-top:8px}

@media print{
  body{padding:12px}
  @page{margin:0.5in;size:auto}
  .page--break{page-break-before:always}  
}
</style>
</head>
<body>
<div class="header">
  <h1>${esc(title)}</h1>
  ${address ? `<p class="header-address">${esc(address)}</p>` : ''}
  <p>${esc(subtitle)}</p>
  <div class="kpis">${kpiHTML}</div>
</div>
${inputsHTML ? `<div class="inputs-section"><div class="inputs-title">Input Parameters</div>${inputsHTML}</div>` : ''}
${pagesHTML}
<div class="timestamp">Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) return
  win.document.write(html)
  win.document.close()
  win.onload = () => {
    // Close tab after print/cancel — try multiple approaches for cross-browser support
    win.addEventListener('afterprint', () => win.close())
    win.matchMedia('print').addEventListener('change', (e) => {
      if (!e.matches) setTimeout(() => win.close(), 300)
    })
    win.print()
  }
}

// ── Formatters (duplicated from calculators for standalone use) ──────────────
export const fmt = {
  currency: (v, d = 0) => {
    if (v === null || v === undefined || !isFinite(v)) return '—'
    const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
    return v < 0 ? `-$${s}` : `$${s}`
  },
  pct: (v, d = 2) => isFinite(v) ? `${(v * 100).toFixed(d)}%` : '—',
  num: (v, d = 2) => isFinite(v) ? v.toFixed(d) : '—',
}

// ── Input Parameters HTML builder ────────────────────────────────────────────
/**
 * @param {Array} groups – [{ title, items: [{ label, value }] }]
 */
export function inputsGridHTML(groups) {
  return `<div class="inputs-grid">${groups.map(g =>
    `<div class="input-group-title">${esc(g.title)}</div>` +
    g.items.map(i =>
      `<div class="input-row"><span class="input-row__label">${esc(i.label)}</span><span class="input-row__value">${esc(i.value)}</span></div>`
    ).join('')
  ).join('')}</div>`
}

// ── Section HTML wrapper ─────────────────────────────────────────────────────
export function sectionHTML(title, body) {
  return `<div class="section"><div class="section-title">${esc(title)}</div><div class="section-body">${body}</div></div>`
}

// ── Section HTML builders ────────────────────────────────────────────────────

export function cardsHTML(cards) {
  return `<div class="cards">${cards.map(c =>
    `<div class="card">
      <div class="card-label">${esc(c.label)}</div>
      <div class="card-value" style="${c.color ? `color:${safeColor(c.color)}` : ''}">${esc(c.value)}</div>
      ${c.sub ? `<div class="card-sub">${esc(c.sub)}</div>` : ''}
    </div>`
  ).join('')}</div>`
}

export function pillsHTML(pills) {
  return `<div class="pills">${pills.map(p =>
    `<div class="pill${p.variant ? ` pill--${safeIdent(p.variant)}` : ''}">
      <div class="pill-label">${esc(p.label)}</div>
      <div class="pill-value">${esc(p.value)}</div>
    </div>`
  ).join('')}</div>`
}

export function plRowsHTML(rows) {
  return rows.map(r => {
    if (r.divider) return `<div class="${r.heavy ? 'pl-divider--heavy' : 'pl-divider'}"></div>`
    const cls = ['pl-row']
    if (r.indent) cls.push('pl-row--indent')
    if (r.bold) cls.push('pl-row--bold')
    return `<div class="${cls.join(' ')}">
      <span>${esc(r.label)}</span>
      <span style="${r.red ? 'color:#CA1551' : ''}">${esc(r.value)}</span>
    </div>`
  }).join('')
}

export function tableHTML(headers, rows) {
  const ths = headers.map(h =>
    `<th style="${h.align === 'right' ? 'text-align:right' : 'text-align:left'}">${esc(h.label)}</th>`
  ).join('')
  const trs = rows.map(r => {
    const cls = r._highlight ? ' class="highlight"' : ''
    const tds = headers.map(h => {
      const val = r[h.key] ?? ''
      const style = h.align === 'right' ? ' style="text-align:right"' : ''
      return `<td${style}>${esc(val)}</td>`
    }).join('')
    return `<tr${cls}>${tds}</tr>`
  }).join('')
  return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`
}

// ── Chart SVG builders ───────────────────────────────────────────────────────

function legendHTML(items) {
  return `<div style="display:flex;flex-wrap:wrap;gap:12px;font-size:11px;color:#66707D;margin-bottom:4px">${
    items.map(i =>
      `<div style="display:flex;align-items:center;gap:4px${i.right ? ';margin-left:auto' : ''}">` +
      (i.swatch ? `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${safeColor(i.swatch)}"></span>` : '') +
      (i.lineSvg || '') +
      `<span${i.bold ? ' style="font-weight:700;color:' + (safeColor(i.color) || '#3C4654') + '"' : ''}>${esc(i.label)}</span></div>`
    ).join('')
  }</div>`
}

/**
 * 30-Year stacked bar + ROI line chart (Rental ROI Projections tab)
 */
export function longTermChartSVG(longTerm, { currency: c, pct: p }) {
  const W = 700, H = 240
  const PAD = { t: 44, r: 20, b: 48, l: 80 }
  const iW = W - PAD.l - PAD.r
  const iH = H - PAD.t - PAD.b
  const displayed = longTerm.filter((_, i) => i < 30)
  const barW = iW / displayed.length
  const maxVal = Math.max(...displayed.map(d => Math.max(0, d.annualCashFlow) + Math.max(0, d.equityAccrued) + Math.max(0, d.appreciation)), 1)
  const minVal = Math.min(...displayed.map(d => Math.min(0, d.annualCashFlow)), 0)
  const totalRange = maxVal - minVal
  const zeroY = PAD.t + (maxVal / totalRange) * iH
  const roiMax = Math.max(...displayed.map(d => d.totalROI), 0.01)
  const roiLine = displayed.map((d, i) => {
    const x = PAD.l + (i + 0.5) * barW
    const y = zeroY - (Math.max(0, d.totalROI) / roiMax) * (zeroY - PAD.t)
    return `${x},${y}`
  }).join(' ')

  const legend = legendHTML([
    { swatch: '#F16722', label: 'Cash Flow (+)' },
    { swatch: '#CA1551', label: 'Cash Flow (-)' },
    { swatch: '#3D9AA7', label: 'Equity' },
    { swatch: '#94C9D1', label: 'Appreciation' },
    { lineSvg: '<svg width="28" height="4"><line x1="0" y1="2" x2="28" y2="2" stroke="#0E6673" stroke-width="3"/></svg>', label: 'Annual Total ROI %', bold: true, color: '#0E6673', right: true },
  ])

  let gridLines = ''
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    const y = PAD.t + f * iH
    const val = maxVal - f * totalRange
    gridLines += `<line x1="${PAD.l}" x2="${PAD.l + iW}" y1="${y}" y2="${y}" stroke="#EFF2F6" stroke-width="1"/>
      <text x="${PAD.l - 6}" y="${y + 5}" text-anchor="end" font-size="14" font-weight="600" fill="#66707D">${c(val, 0)}</text>`
  }

  let zeroLine = ''
  if (minVal < 0) {
    zeroLine = `<line x1="${PAD.l}" x2="${PAD.l + iW}" y1="${zeroY}" y2="${zeroY}" stroke="#99A0AA" stroke-width="2"/>`
  }

  let bars = ''
  displayed.forEach((d, i) => {
    const x = PAD.l + i * barW + barW * 0.12
    const bw = barW * 0.76
    const showLabel = displayed.length <= 15 || (i + 1) % 5 === 0 || i === 0
    const cf = d.annualCashFlow
    const cfPosH = (Math.max(0, cf) / totalRange) * iH
    const cfNegH = (Math.max(0, -cf) / totalRange) * iH
    const h2 = (Math.max(0, d.equityAccrued) / totalRange) * iH
    const h3 = (Math.max(0, d.appreciation) / totalRange) * iH
    const stackTop = zeroY - cfPosH - h2 - h3
    const stackBot = zeroY + cfNegH
    const stackH = stackBot - stackTop
    const clipId = `bar-clip-p-${d.year}`
    bars += `<defs><clipPath id="${clipId}"><rect x="${x}" y="${stackTop}" width="${bw}" height="${stackH}" rx="2.5"/></clipPath></defs>
      <g clip-path="url(#${clipId})">`
    if (cfPosH > 0) bars += `<rect x="${x}" y="${zeroY - cfPosH}" width="${bw}" height="${cfPosH}" fill="#F16722"/>`
    if (cfNegH > 0) bars += `<rect x="${x}" y="${zeroY}" width="${bw}" height="${cfNegH}" fill="#CA1551"/>`
    bars += `<rect x="${x}" y="${zeroY - cfPosH - h2}" width="${bw}" height="${h2}" fill="#3D9AA7"/>
      <rect x="${x}" y="${zeroY - cfPosH - h2 - h3}" width="${bw}" height="${h3}" fill="#94C9D1"/>
      </g>`
    if (showLabel) bars += `<text x="${x + bw / 2}" y="${H - PAD.b + 16}" text-anchor="middle" font-size="14" font-weight="600" fill="#66707D">Y${d.year}</text>`
  })

  let roiDots = ''
  displayed.filter((_, i) => i % 5 === 0 || i === displayed.length - 1).forEach(d => {
    const idx = displayed.indexOf(d)
    const x = PAD.l + (idx + 0.5) * barW
    const y = zeroY - (Math.max(0, d.totalROI) / roiMax) * (zeroY - PAD.t)
    const pillW = 48, pillH = 21, pillR = 6
    const pillX = x - pillW / 2, pillY = y - pillH - 12
    roiDots += `<circle cx="${x}" cy="${y}" r="5" fill="#0E6673" stroke="white" stroke-width="2"/>
      <rect x="${pillX}" y="${pillY}" width="${pillW}" height="${pillH}" rx="${pillR}" fill="#0E6673"/>
      <polygon points="${x - 4},${pillY + pillH} ${x + 4},${pillY + pillH} ${x},${pillY + pillH + 5}" fill="#0E6673"/>
      <text x="${x}" y="${pillY + 14.5}" text-anchor="middle" font-size="12" font-weight="700" fill="white">${p(d.totalROI, 2)}</text>`
  })

  return legend +
    `<svg viewBox="0 0 ${W} ${H}" style="width:100%;height:240px">
      ${gridLines}${zeroLine}${bars}
      <polyline points="${roiLine}" fill="none" stroke="#0E6673" stroke-width="3" stroke-linejoin="round"/>
      ${roiDots}
    </svg>`
}

/**
 * CoC Sensitivity line chart (Rental ROI Sensitivity tab)
 */
export function cocSensitivityChartSVG(data, currentPct, { pct: p }) {
  const W = 560, H = 180
  const PAD = { t: 16, r: 20, b: 40, l: 56 }
  const iW = W - PAD.l - PAD.r
  const iH = H - PAD.t - PAD.b
  const minCoc = Math.min(...data.map(d => d.cocROI))
  const maxCoc = Math.max(...data.map(d => d.cocROI))
  const range = maxCoc - minCoc || 0.01
  const pts = data.map((d, i) => {
    const x = PAD.l + (i / (data.length - 1)) * iW
    const y = PAD.t + iH - ((d.cocROI - minCoc) / range) * iH
    return `${x},${y}`
  }).join(' ')
  const curIdx = data.findIndex(d => d.pct === Math.round(currentPct * 100))
  const curD = curIdx >= 0 ? data[curIdx] : null
  const curX = curD ? PAD.l + (curIdx / (data.length - 1)) * iW : null
  const curY = curD ? PAD.t + iH - ((curD.cocROI - minCoc) / range) * iH : null

  let gridLines = ''
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    const y = PAD.t + iH * f
    const v = maxCoc - f * range
    gridLines += `<line x1="${PAD.l}" x2="${PAD.l + iW}" y1="${y}" y2="${y}" stroke="#EFF2F6" stroke-width="1"/>
      <text x="${PAD.l - 6}" y="${y + 4}" text-anchor="end" font-size="9" fill="#66707D">${p(v, 2)}</text>`
  }

  let xLabels = ''
  data.forEach((d, i) => {
    const x = PAD.l + (i / (data.length - 1)) * iW
    if (d.pct % 5 === 0) xLabels += `<text x="${x}" y="${H - PAD.b + 13}" text-anchor="middle" font-size="9" fill="#66707D">${d.pct}%</text>`
  })

  let curMark = ''
  if (curX && curY) {
    curMark = `<line x1="${curX}" y1="${PAD.t}" x2="${curX}" y2="${PAD.t + iH}" stroke="#F16722" stroke-width="1.5" stroke-dasharray="4 2"/>
      <circle cx="${curX}" cy="${curY}" r="5" fill="#F16722" stroke="white" stroke-width="1.5"/>
      <rect x="${curX - 28}" y="${curY - 22}" width="56" height="16" rx="4" fill="#F16722"/>
      <text x="${curX}" y="${curY - 11}" text-anchor="middle" font-size="9" font-weight="bold" fill="white">${p(curD.cocROI, 2)}</text>`
  }

  return `<p style="font-size:11px;color:#66707D;margin:0 0 4px">How cash-on-cash ROI changes with down payment % — all other inputs constant</p>
    <svg viewBox="0 0 ${W} ${H}" style="width:100%;height:200px">
      ${gridLines}${xLabels}
      <text x="${W / 2}" y="${H - 4}" text-anchor="middle" font-size="9" fill="#66707D">Down Payment %</text>
      <polyline points="${pts}" fill="none" stroke="#128292" stroke-width="2" stroke-linejoin="round"/>
      ${curMark}
    </svg>`
}

/**
 * Cost breakdown horizontal bar chart (Fix & Flip Summary tab)
 */
export function costBreakdownChartSVG(r, { currency: c }) {
  const W = 640, H = 150
  const PAD = { t: 40, r: 20, b: 44, l: 20 }
  const iW = W - PAD.l - PAD.r
  const barH = 32

  const segs = [
    { label: 'Purchase', value: r.purchaseCostTotal, color: '#128292' },
    { label: 'Holding',  value: r.holdingCostTotal,  color: '#3D9AA7' },
    { label: 'Rehab',    value: r.rehabCostTotal,    color: '#F16722' },
    { label: 'Selling',  value: r.sellingCostTotal,  color: '#CA1551' },
  ]
  const maxVal = Math.max(r.totalCosts, r.salePrice, 1) * 1.05
  const profit = r.totalProfit >= 0
  const lineColor = profit ? '#5AC471' : '#CA1551'

  const legend = legendHTML([
    ...segs.map(s => ({ swatch: s.color, label: s.label })),
    { lineSvg: `<svg width="20" height="3"><line x1="0" y1="1.5" x2="20" y2="1.5" stroke="${lineColor}" stroke-width="2" stroke-dasharray="4 2"/></svg>`, label: 'Sale Price', right: true },
  ])

  let xCursor = PAD.l
  let barRects = ''
  let barLabels = ''
  segs.forEach(seg => {
    const w = (seg.value / maxVal) * iW
    barRects += `<rect x="${xCursor}" y="${PAD.t}" width="${Math.max(w, 1)}" height="${barH}" fill="${seg.color}"/>`
    if (w > 50) barLabels += `<text x="${xCursor + w / 2}" y="${PAD.t + barH / 2 + 4}" text-anchor="middle" font-size="10" font-weight="bold" fill="white">${seg.label}</text>`
    xCursor += w
  })

  const spX = PAD.l + (r.salePrice / maxVal) * iW
  const tcX = PAD.l + (r.totalCosts / maxVal) * iW

  let xAxisLabels = ''
  for (const f of [0, 0.25, 0.5, 0.75, 1]) {
    xAxisLabels += `<text x="${PAD.l + f * iW}" y="${PAD.t + barH + 16}" text-anchor="middle" font-size="11" font-weight="600" fill="#66707D">${c(maxVal * f, 0)}</text>`
  }

  return legend +
    `<svg viewBox="0 0 ${W} ${H}" style="width:100%;height:150px">
      <defs><clipPath id="flip-bar-clip-p"><rect x="${PAD.l}" y="${PAD.t}" width="${iW}" height="${barH}" rx="4"/></clipPath></defs>
      <rect x="${PAD.l}" y="${PAD.t}" width="${iW}" height="${barH}" rx="4" fill="#EFF2F6"/>
      <g clip-path="url(#flip-bar-clip-p)">${barRects}</g>
      ${barLabels}
      <line x1="${tcX}" y1="${PAD.t - 2}" x2="${tcX}" y2="${PAD.t + barH + 2}" stroke="#66707D" stroke-width="1"/>
      <text x="${tcX}" y="${PAD.t - 6}" text-anchor="middle" font-size="11" font-weight="600" fill="#66707D">Total Costs</text>
      <line x1="${spX}" y1="${PAD.t - 14}" x2="${spX}" y2="${PAD.t + barH + 16}" stroke="${lineColor}" stroke-width="2" stroke-dasharray="4 2"/>
      <text x="${spX}" y="${PAD.t - 18}" text-anchor="middle" font-size="11" font-weight="700" fill="${lineColor}">Sale Price</text>
      ${xAxisLabels}
    </svg>`
}
